import { useState } from "react";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  ExternalLink
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PORTAL_URL = process.env.REACT_APP_PORTAL_URL;

const serviceOptions = [
  { value: "individual-tax", label: "Individual Tax Preparation" },
  { value: "business-tax", label: "Business Tax" },
  { value: "bookkeeping", label: "Bookkeeping" },
  { value: "tax-planning", label: "Tax Planning" },
  { value: "notice-help", label: "IRS/State Notice Help" },
  { value: "other", label: "Other / Not Sure" }
];

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service_needed: "",
    message: "",
    sms_consent: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contacts`, formData);
      toast.success("Thank you! We'll be in touch within 1-2 business days.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service_needed: "",
        message: "",
        sms_consent: false
      });
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="bg-navy text-white noise-overlay py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-mono text-bronze text-sm mb-4 tracking-wider uppercase">
              Contact Us
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Get in Touch
            </h1>
            <p className="font-body text-lg md:text-xl text-gray-300">
              Have questions? Ready to get started? We'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="bg-bone py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info & Map */}
            <div className="space-y-8">
              {/* Contact Details */}
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-8">
                  <h2 className="font-heading text-2xl font-bold text-navy mb-6">
                    Contact Information
                  </h2>
                  <div className="space-y-6">
                    <a 
                      href="tel:+12012537674" 
                      className="flex items-center gap-4 group"
                      data-testid="contact-phone"
                    >
                      <div className="w-12 h-12 rounded-full bg-bone flex items-center justify-center group-hover:bg-bronze/20 transition-colors">
                        <Phone className="w-6 h-6 text-bronze" />
                      </div>
                      <div>
                        <p className="font-body text-sm text-gray-500">Phone</p>
                        <p className="font-body font-semibold text-navy">201-253-7674</p>
                      </div>
                    </a>
                    <a 
                      href="mailto:INFO@DATAXGUY.COM" 
                      className="flex items-center gap-4 group"
                      data-testid="contact-email"
                    >
                      <div className="w-12 h-12 rounded-full bg-bone flex items-center justify-center group-hover:bg-bronze/20 transition-colors">
                        <Mail className="w-6 h-6 text-bronze" />
                      </div>
                      <div>
                        <p className="font-body text-sm text-gray-500">Email</p>
                        <p className="font-body font-semibold text-navy">INFO@DATAXGUY.COM</p>
                      </div>
                    </a>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-bone flex items-center justify-center">
                        <MapPin className="w-6 h-6 text-bronze" />
                      </div>
                      <div>
                        <p className="font-body text-sm text-gray-500">Location</p>
                        <p className="font-body font-semibold text-navy">New York, NY</p>
                        <p className="font-body text-sm text-gray-500">Virtual Services Available</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-bone flex items-center justify-center">
                        <Clock className="w-6 h-6 text-bronze" />
                      </div>
                      <div>
                        <p className="font-body text-sm text-gray-500">Hours</p>
                        <p className="font-body font-semibold text-navy">Mon-Fri: 9AM - 6PM</p>
                        <p className="font-body text-sm text-gray-500">Sat: By Appointment</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Map Placeholder */}
              <Card className="bg-white border-0 shadow-sm overflow-hidden">
                <div className="h-64 bg-gray-200 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="font-body text-gray-500">New York, NY</p>
                    <p className="font-body text-sm text-gray-400">Virtual-First Practice</p>
                  </div>
                </div>
              </Card>

              {/* Quick Links */}
              <Card className="bg-navy text-white">
                <CardContent className="p-8">
                  <h3 className="font-heading text-xl font-bold mb-4">
                    Already a Client?
                  </h3>
                  <p className="font-body text-gray-300 mb-6">
                    Access your portal to upload documents, view your returns, and communicate securely.
                  </p>
                  <Button
                    asChild
                    className="w-full bg-bronze hover:bg-bronze/90 text-navy rounded-full font-medium"
                    data-testid="contact-portal-btn"
                  >
                    <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer">
                      Client Portal Login
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <Card className="bg-white border-0 shadow-lg">
              <CardContent className="p-8">
                <h2 className="font-heading text-2xl font-bold text-navy mb-2">
                  Send Us a Message
                </h2>
                <p className="font-body text-gray-600 mb-8">
                  Fill out the form below and we'll get back to you within 1-2 business days.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-body">Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="h-12 rounded-lg"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-body">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="h-12 rounded-lg"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="font-body">Phone</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="h-12 rounded-lg"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service" className="font-body">Service Needed *</Label>
                    <Select
                      value={formData.service_needed}
                      onValueChange={(value) => setFormData({...formData, service_needed: value})}
                      required
                    >
                      <SelectTrigger className="h-12 rounded-lg" data-testid="contact-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {serviceOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-body">Message *</Label>
                    <Textarea
                      id="message"
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Tell us about your tax or accounting needs..."
                      className="rounded-lg"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="sms_consent"
                      checked={formData.sms_consent}
                      onCheckedChange={(checked) => setFormData({...formData, sms_consent: checked})}
                      className="mt-1"
                      data-testid="contact-sms-checkbox"
                    />
                    <Label htmlFor="sms_consent" className="font-body text-sm text-gray-600 leading-relaxed">
                      I consent to receive SMS/text messages and emails from DA TAXGUY & ASSOCIATES regarding my inquiry. Message frequency varies. Reply STOP to opt out. Standard message rates may apply.
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-navy hover:bg-gray-800 text-white rounded-full py-6 text-lg font-medium"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? "Sending..." : "Send Message"}
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>

                {/* Alternative CTA */}
                <div className="mt-8 pt-8 border-t border-gray-100 text-center">
                  <p className="font-body text-gray-600 mb-4">
                    Prefer to schedule a call?
                  </p>
                  <Button
                    asChild
                    variant="outline"
                    className="rounded-full font-medium"
                    data-testid="contact-book-btn"
                  >
                    <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                      Book Free Consultation
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
